import { createTheme } from '@mui/material/styles';
import { paletteTokens } from '../generated/paletteVariables';
import type { Theme } from '../useTheme';
import { getLightThemeVariables } from '../generated/lightThemeVariables';
import { themeOptions } from './themeOptions';

export const getLightThemeOptions = (palette = paletteTokens) =>
  ({
    palette: {
      mode: 'light',
      ...palette,
      common: {
        black: '#000000',
        white: '#ffffff',
      },
      primary: {
        main: '#66cccc',
        dark: '#012F33',
        light: '#DDEBEC',
      },
      error: {
        main: '#e30202',
        dark: '#E68E5B',
        light: '#FEF5EC',
        offline: '#515151',
      },
      warning: { main: '#E68E5B', light: '#FC856D', busy: '#F43350' },
      success: { main: '#44b700', online: '#21C17B' },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      text: {
        primary: '#ffffff',
        body: '#fafafa',
        secondary: 'rgba(255, 255, 255, 0.8)',
        disabled: '#000000',
      },
      background: {
        primary: '#1c1c1c',
        secondary: '#303030',
        tertiary: '#e8e9f1',
        canvas: '#1c1c1c',
        default: '#000000',
        paper: '#ffffff',
        light: 'rgba(255, 255, 255, 0.3)',
        panel: palette.neutral.darkAlpha[200],
        panelInside: palette.neutral.lightAlpha[500],
        activeHighlight: '#E68E5B',
        checkInSmallOrMediumViewportBackgroundColor: 'rgba(255, 255, 255, 1)',
        /**
         * @TODO rename according to design when it got a proper name.
         * @description https://www.figma.com/design/XwyS8xx8biP2SaBDeCyfwF/Doxy.me-Design-System-(New)?type=design&node-id=1197-35843&mode=design&t=QcmjnxSxvZjvQd13-0
         */
        fab: 'rgba(0, 0, 0, 0.7)',
        interactive: 'rgba(0, 0, 0, 0.4)',
        screenshareCanvas: 'rgba(1, 38, 41, 1)',
      },
      actions: {
        muted: '#E68E5B',
        mutedHover: '#CE7846',
        hangup: '#E30202',
        hangupHover: '#CB0000',
      },
    },
    shadow: {
      button: {
        primary:
          '0px 2px 5px 0px rgba(0, 0, 0, 0.05), 0px 0.75px 3.5px 0px rgba(0, 0, 0, 0.08), 0px 0.15px 0.8px 0px rgba(0, 0, 0, 0.06)',
        secondary:
          '0px 2px 12px 0px rgba(0, 0, 0, 0.02), 0px 4px 8px 0px rgba(0, 0, 0, 0.03), 0px 2px 4px 0px rgba(0, 0, 0, 0.04), 0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 1px 0px 0px rgba(0, 0, 0, 0.06), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.08)',
        google:
          'rgba(0, 0, 0, 0.02) 0px 1px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px, rgba(0, 0, 0, 0.02) 0px -4px 4px 0px inset',
      },
    },
    ...getLightThemeVariables(palette),
    ...themeOptions,
  }) as const;

export const getLightTheme = (palette = paletteTokens): Theme =>
  createTheme(getLightThemeOptions(palette)) as Theme;
